"use client";
import Link from "next/link";
import Button from "@/components/primitives/button";
import {
  BestOdds,
  GameAlignment,
  GameAlignmentType,
  GameOutcome2,
  GameRecord,
  LineType,
  ScoreboardGame,
  StartingPlayer as StartingPlayerInfo,
  TeamStreak,
  formatOddOutcomeShort,
  oddOutcomeColor,
  toSportType,
} from "common";
import OddsGroup from "../odds/group";
import GameTimestamp from "../timestamp";
import TeamLogo from "@/components/team/logo";
import { formatGameStatus } from "@/utils/game";
import Chip from "@/components/primitives/chip";
import Icon from "@/components/primitives/icon";
import PickChip from "../detail/pick-chip";
import * as Collapsible from "@radix-ui/react-collapsible";
import GameOddsChartTabber from "../performance-chart";
import { truncateName } from "@/utils/injuries";
import React from "react";
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import "@splidejs/react-splide/css/core";
import { formatOdd } from "@/utils";
import { StartingLineupTable } from "@/components/global/starting-lineup";
import NotFound from "@/components/primitives/not-found";
import { GameInjuriesTable } from "@/components/global/latest-injuries";
import GameDetailScore from "../detail/score";
import { Boxscore } from "./hero";
import { useOddsFormat } from "@/utils/providers/odds-format";
import GameCardSeriesChip from "./series-chip";
import { useGame } from "@/utils/providers/game";
export interface GameCardDetailedProps {
  game?: Partial<ScoreboardGame>;
  next?: boolean;
}

function GameCardDetailed({
  index,
  next,
  teamColor,
}: {
  index: number;
  next?: boolean;
  teamColor?: string;
}) {
  const ctx = useGame();

  console.log("away injuries", ctx.game.awayInjuries);

  const innerHtml: JSX.Element = (
    <>
      <div className="absolute w-full left-0 right-0 -top-3 flex justify-center z-[1]">
        <div className="flex items-center justify-center">
          {ctx.game.series && (
            <GameCardSeriesChip
              info={ctx.game.series}
              neutral={ctx.closed || next !== undefined}
            />
          )}
        </div>
      </div>
      <div
        className="absolute inset-0 w-full h-2 rounded-tr-xl rounded-tl-xl"
        style={{
          backgroundImage: `linear-gradient(to right, #${ctx.game.awayTeam.primaryColor} 41.63%, #${ctx.game.homeTeam.primaryColor} 57.87%)`,
        }}
      ></div>
      {/* <Link
        href={createGameDetailSlug(game.league, game.id)}
        className="absolute inset-0 h-full w-full z-0 bg-transparent rounded-xl"
      ></Link> */}
      {/* 3 COLUMN LAYOUT - SECTION 1A */}
      <div className="grid grid-cols-[1fr_146px_1fr] mt-1 relative gap-0 w-full">
        {/* LEFT COL */}
        <div className="flex flex-col items-center">
          {/* AWAY TEAM */}
          <div
            className={`flex flex-col items-center mt-2 ${
              ctx.game.status === "Scheduled" ? "pr-0" : "pr-4"
            }`}
          >
            <TeamLogo
              team={ctx.game.awayTeam}
              sport={ctx.game.league}
              size="md"
            />
            <div className="text-title-sm-medium text-secondary-300 whitespace-nowrap flex flex-row gap-0.5">
              <span>{ctx.game.awayTeam.abbreviation}</span>
              {/* <span className="hidden sm:block">{game.awayTeam.name}</span> */}
            </div>
            {ctx.game.status !== "InProgress" && (
              <div className="text-body-sm text-secondary-300 mt-1">
                {ctx.game.league === "nhl" ? (
                  <>
                    {ctx.game.awayRecord.wins}-{ctx.game.awayRecord.losses}-
                    {ctx.game.awayRecord.overtimeLosses}
                  </>
                ) : (
                  <>
                    {ctx.game.awayRecord.wins}-{ctx.game.awayRecord.losses}
                  </>
                )}
              </div>
            )}
            <div className="text-label-lg-medium text-secondary-400 mt-1">
              AWAY TEAM
            </div>
          </div>
          {ctx.game.status === "Scheduled" &&
            ctx.game.awayLast10 &&
            ctx.game.awayStreak && (
              <>
                {/* DIVIDER */}
                <div className="w-8 h-px bg-surface-500 my-[10px]"></div>
                {/* LAST 10 & LAST 5 */}
                <div className="flex flex-col items-center gap-1">
                  <Last10Record data={ctx.game.awayLast10} />
                  <Last10Streak data={ctx.game.awayStreak} />
                </div>
              </>
            )}
        </div>
        {/* CENTER COL */}
        <div className="flex flex-col justify-start items-center">
          {/* PLACEHOLDER GAP */}
          <div className="h-10 w-full"></div>
          {/* ANCHORED TO SECTION 1A */}
          <div className="absolute top-0 left-0 right-0 w-full flex justify-center">
            <GameTimestamp
              type="stacked"
              ctx={ctx}
              dateTime={ctx.game.dateTime}
              platform={ctx.game.platform}
            />
          </div>
          {ctx.game.status === "Scheduled" ? (
            <>
              {/* VIEW GAME - SCHEDULED ONLY */}
              <div className="mt-1">
                <Button href={ctx.slug} scroll={true} size="xs" forceButton>
                  View Game
                </Button>
              </div>
              <div className="mt-2">
                <PickChip
                  totalPicks={ctx.game.totalPicks}
                  totalBettors={ctx.game.totalBettors || 0}
                  totalPicksForSale={ctx.game.totalPicksForSale}
                  avatars={[]}
                  totalComments={ctx.game.discussion.total_comments}
                />
              </div>
              {ctx.game.bestOdds?.length ? (
                <>
                  {/* BET NOW SLIDER - SCHEDULED ONLY */}

                  <div className="mt-[10px] w-full flex justify-center">
                    {/* PLACEHOLDER GAP */}
                    <div className="h-[88px] w-[114px] bg-black rounded-md relative overflow-hidden">
                      <div className="text-label-lg-bold text-white text-center p-0.5 relative z-[1]">
                        BEST ODDS
                      </div>
                      <div className="inset-0 bg-[url('/images/ui/best-odds-slider-bg.png')] bg-no-repeat bg-top w-full h-full absolute z-1"></div>

                      <SportsbookCarousel
                        awayTeam={ctx.game.awayTeam.abbreviation}
                        homeTeam={ctx.game.homeTeam.abbreviation}
                        data={ctx.game.bestOdds || []}
                        gameIndex={index}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-[10px] w-full flex justify-center">
                  {/* PLACEHOLDER GAP */}
                  <div className="h-[88px] w-[114px] bg-surface-400 rounded-md relative overflow-hidden flex justify-center items-center">
                    <div className="text-label-lg-medium text-secondary-500 text-center p-0.5">
                      BEST ODDS <br></br>TBD
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : ctx.game.status === "Canceled" ||
            ctx.game.status === "Postponed" ? (
            <div className="flex flex-col justify-center items-center mt-1">
              <Chip color="white">
                <span className="normal-case">
                  {ctx.isLive ? ctx.game.statusDescription: formatGameStatus(ctx.game)}
                </span>
              </Chip>
              <div className="mt-2">
                <Button href={ctx.slug} scroll={true} size="xs" forceButton>
                  View Game
                </Button>
              </div>
            </div>
          ) : (
            <GameDetailScore size="md" />
          )}
        </div>
        {/* RIGHT COL */}
        <div className="flex flex-col items-center">
          {/* HOME TEAM */}
          <div
            className={`flex flex-col items-center mt-2 ${
              ctx.game.status === "Scheduled" ? "pr-0" : "pl-4"
            }`}
          >
            <TeamLogo
              team={ctx.game.homeTeam}
              sport={ctx.game.league}
              size="md"
            />
            <div className="text-title-sm-medium text-secondary-300 whitespace-nowrap flex flex-row gap-0.5">
              <span>{ctx.game.homeTeam.abbreviation}</span>
              {/* <span className="hidden sm:block">{game.homeTeam.name}</span> */}
            </div>
            {ctx.game.status !== "InProgress" && (
              <div className="text-body-sm text-secondary-300 mt-1">
                {ctx.game.league === "nhl" ? (
                  <>
                    {ctx.game.homeRecord.wins}-{ctx.game.homeRecord.losses}-
                    {ctx.game.homeRecord.overtimeLosses}
                  </>
                ) : (
                  <>
                    {ctx.game.homeRecord.wins}-{ctx.game.homeRecord.losses}
                  </>
                )}
              </div>
            )}
            <div className="text-label-lg-medium text-secondary-400 mt-1">
              HOME TEAM
            </div>
          </div>
          {ctx.game.status === "Scheduled" &&
            ctx.game.homeLast10 &&
            ctx.game.homeStreak && (
              <>
                {/* DIVIDER */}
                <div className="w-8 h-px bg-surface-500 my-[10px]"></div>
                {/* LAST 10 & LAST 5 */}
                <div className="flex flex-col items-center gap-1">
                  <Last10Record data={ctx.game.homeLast10} />
                  <Last10Streak data={ctx.game.homeStreak} />
                </div>
              </>
            )}
        </div>
      </div>
      {ctx.game.status === "Scheduled" ? (
        <>
          {/* TOP LEADERS & STADIUM 1B */}
          <div className="mt-2 p-2 rounded-md bg-surface-400 text-secondary-300 w-full">
            <div className="grid grid-cols-[1fr_146px_1fr] relative gap-0">
              <StartingPlayer
                data={ctx.game.awayStartingPlayer}
                alignment="away"
              />
              <div className="flex flex-col items-center p-[6px]">
                <div className="text-label-sm-medium text-secondary-500 max-w-[80px] text-center uppercase">
                  {ctx.game.league === "mlb" ? (
                    <>STARTING PITCHERS</>
                  ) : ctx.game.league === "nfl" ? (
                    <>STARTING QUARTERBACKS</>
                  ) : ctx.game.league === "nba" ? (
                    <>PLAYERS TO WATCH</>
                  ) : (
                    <>STARTING GOALIES</>
                  )}
                </div>
                <div className="w-8 h-px my-[10px] bg-secondary-700"></div>
                <div className="text-label-lg-medium text-secondary-300 text-center max-w-[100px] h-[28px] items-center">
                  {ctx.game.stadium?.name ||
                    `${ctx.game.league === "nfl" ? "Stadium" : "Arena"} N/A`}
                </div>
              </div>
              <StartingPlayer
                data={ctx.game.homeStartingPlayer}
                alignment="home"
              />
            </div>
            {/* STARTING LINEUP COLLAPSIBLE */}
            <Collapsible.Root className="group">
              <Collapsible.Trigger className="grid grid-cols-[1fr_146px_1fr] relative gap-0 bg-surface-700 rounded-md w-full p-2 mt-2 group-data-[state=open]:bg-secondary-300 text-secondary-300 group-data-[state=open]:rounded-br-none group-data-[state=open]:rounded-bl-none">
                <div className="text-label-lg-medium text-center">
                  {ctx.game.awayTeam.abbreviation}
                </div>
                <div className="text-label-lg-bold flex flex-row justify-center items-center gap-1">
                  <div className="group-data-[state=open]:hidden block">
                    <Icon
                      name="caret-down"
                      size="xs"
                      className="group-data-[state=open]:fill-secondary-400"
                    />
                  </div>
                  <div className="group-data-[state=open]:block hidden">
                    <Icon
                      name="caret-up"
                      size="xs"
                      className="fill-surface-700"
                    />
                  </div>
                  <span className="group-data-[state=open]:text-surface-700 text-secondary-300">
                    STARTING LINEUP
                  </span>
                  <div className="group-data-[state=open]:hidden block">
                    <Icon
                      name="caret-down"
                      size="xs"
                      className="group-data-[state=open]:fill-secondary-400"
                    />
                  </div>
                  <div className="group-data-[state=open]:block hidden">
                    <Icon
                      name="caret-up"
                      size="xs"
                      className="fill-surface-700"
                    />
                  </div>
                </div>
                <div className="text-label-lg-medium text-center">
                  {ctx.game.homeTeam.abbreviation}
                </div>
              </Collapsible.Trigger>
              <Collapsible.Content className="data-[state=open]:animate-unCollapse data-[state=closed]:animate-collapse overflow-hidden">
                {ctx.game.startingLineup &&
                (ctx.game.startingLineup.away.length ||
                  ctx.game.startingLineup.home.length) ? (
                  <StartingLineupTable
                    data={ctx.game.startingLineup}
                    sport={ctx.game.league}
                  />
                ) : (
                  <div className="bg-surface-700 p-2 rounded-br-md rounded-bl-md transition-all duration-300 ease-in-out pt-4">
                    <NotFound label="Lineup TBD" />
                  </div>
                )}
              </Collapsible.Content>
            </Collapsible.Root>
            {/* INJURIES COLLAPSIBLE */}
            <Collapsible.Root className="group">
              <Collapsible.Trigger className="grid grid-cols-[1fr_146px_1fr] relative gap-0 bg-surface-700 rounded-md w-full p-2 mt-2 group-data-[state=open]:rounded-br-none group-data-[state=open]:rounded-bl-none group-data-[state=open]:bg-secondary-300 text-secondary-300">
                <div className="text-label-lg-medium text-center">
                  {ctx.game.awayTeam.abbreviation}{" "}
                  {ctx.game.awayInjuries && ctx.game.awayInjuries.length > 0
                    ? `(${ctx.game.awayInjuries.length})`
                    : ""}
                </div>
                <div className="text-label-lg-bold flex flex-row justify-center items-center gap-1">
                  <div className="group-data-[state=open]:hidden block">
                    <Icon
                      name="caret-down"
                      size="xs"
                      className="group-data-[state=open]:fill-secondary-400"
                    />
                  </div>
                  <div className="group-data-[state=open]:block hidden">
                    <Icon
                      name="caret-up"
                      size="xs"
                      className="fill-surface-700"
                    />
                  </div>
                  <span className="group-data-[state=open]:text-surface-700 text-secondary-300">
                    LATEST INJURIES
                  </span>
                  <div className="group-data-[state=open]:hidden block">
                    <Icon
                      name="caret-down"
                      size="xs"
                      className="group-data-[state=open]:fill-secondary-400"
                    />
                  </div>
                  <div className="group-data-[state=open]:block hidden">
                    <Icon
                      name="caret-up"
                      size="xs"
                      className="fill-surface-700"
                    />
                  </div>
                </div>
                <div className="text-label-lg-medium text-center">
                  {ctx.game.homeTeam.abbreviation}{" "}
                  {ctx.game.homeInjuries && ctx.game.homeInjuries.length > 0
                    ? `(${ctx.game.homeInjuries.length})`
                    : ""}
                </div>
              </Collapsible.Trigger>
              <Collapsible.Content className="data-[state=open]:animate-unCollapse data-[state=closed]:animate-collapse overflow-hidden">
                {(ctx.game.homeInjuries && ctx.game.homeInjuries.length) ||
                (ctx.game.awayInjuries && ctx.game.awayInjuries.length) ? (
                  <GameInjuriesTable
                    data={{
                      away: ctx.game.awayInjuries,
                      home: ctx.game.homeInjuries,
                    }}
                    sport={toSportType(ctx.game.league)}
                  />
                ) : (
                  <div className="bg-surface-700 p-2 rounded-br-md rounded-bl-md transition-all duration-300 ease-in-out pt-4">
                    <NotFound label="No injuries" />
                  </div>
                )}
              </Collapsible.Content>
            </Collapsible.Root>
          </div>
          <div className="mt-3 w-full">
            <GameOddsChartTabber defaultOdd="moneyline" game={ctx.game} />
          </div>
          <OddsGroup
            lockSide="right"
            game={ctx.game}
            record
            color="light"
            startsNow={ctx.startsNow}
          />
        </>
      ) : (
        <div className="mt-2">
          {ctx.isLive && ctx.game.league === "mlb" && (
            <div className="flex w-full h-[82px] items-center justify-center -mt-8 mb-2 gap-6">
              <div className="relative w-[33px]">
                <div className="inline-flex gap-[5px] flex-row flex-wrap w-[33px] rotate-45">
                  <div
                    className={`w-[14px] h-[14px] border-[2px] ${
                      ctx.currentPeriod?.onSecond
                        ? "bg-primary-500 border-primary-500"
                        : "bg-transparent border-secondary-400"
                    }`}
                  ></div>
                  <div
                    className={`w-[14px] h-[14px] border-[2px] ${
                      ctx.currentPeriod?.onFirst
                        ? "bg-primary-500 border-primary-500"
                        : "bg-transparent border-secondary-400"
                    }`}
                  ></div>
                  <div
                    className={`w-[14px] h-[14px] border-[2px] ${
                      ctx.currentPeriod?.onThird
                        ? "bg-primary-500 border-primary-500"
                        : "bg-transparent border-secondary-400"
                    }`}
                  ></div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-row items-center text-title-sm-bold text-secondary-400 gap-2">
                  <span>B:</span>
                  <div className="flex flex-row gap-1 items-center">
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.balls !== undefined &&
                        ctx.currentPeriod?.balls >= 1
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.balls !== undefined &&
                        ctx.currentPeriod?.balls >= 2
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.balls !== undefined &&
                        ctx.currentPeriod?.balls >= 3
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.balls !== undefined &&
                        ctx.currentPeriod?.balls >= 4
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                  </div>
                </div>
                <div className="flex flex-row items-center text-title-sm-bold text-secondary-400 gap-2">
                  <span>S:</span>
                  <div className="flex flex-row gap-1 items-center">
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.strikes !== undefined &&
                        ctx.currentPeriod?.strikes >= 1
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.strikes !== undefined &&
                        ctx.currentPeriod?.strikes >= 2
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.strikes !== undefined &&
                        ctx.currentPeriod?.strikes >= 3
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                  </div>
                </div>
                <div className="flex flex-row items-center text-title-sm-bold text-secondary-400 gap-2">
                  <span>O:</span>
                  <div className="flex flex-row gap-1 items-center">
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.outs !== undefined &&
                        ctx.currentPeriod?.outs >= 1
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.outs !== undefined &&
                        ctx.currentPeriod?.outs >= 2
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                    <div
                      className={`w-[10px] h-[10px] rounded-full border-[2px] ${
                        ctx.currentPeriod?.outs !== undefined &&
                        ctx.currentPeriod?.outs >= 3
                          ? "bg-primary-500 border-primary-500"
                          : "bg-transparent border-secondary-400"
                      }`}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <Boxscore game={ctx.game} mobile size="sm" />
          <div className="flex justify-center items-center mt-2 mb-6">
            <div className="text-label-lg-medium text-secondary-300 text-center items-center">
              {ctx.game.stadium?.name ||
                `${ctx.game.league === "nfl" ? "Stadium" : "Arena"} N/A`}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-4 left-0 right-0 z-20 flex justify-center">
              <PickChip
                totalPicks={ctx.game.totalPicks}
                showAvatars={false}
                avatars={[]}
                totalPicksForSale={0}
                totalBettors={0}
              />
            </div>
            <OddsGroup
              lockSide="right"
              game={ctx.game}
              record={ctx.game.status !== "InProgress"}
              startsNow={ctx.startsNow}
            />
          </div>
        </div>
      )}
    </>
  );

  if (ctx.game) {
    if (ctx.game.status === "Scheduled") {
      return (
        <div
          className={`w-full pt-4 pb-2 px-3 rounded-xl relative shadow-card h-fit bg-surface-700 transition-all duration-300 ease-in-out flex flex-col justify-start`}
        >
          {innerHtml}
        </div>
      );
    }
    return (
      <Link
        data-prevent-nprogress={true}
        href={ctx.slug}
        className={`w-full pt-4 pb-2 px-3 rounded-xl relative h-fit ${
          ctx.closed ? "bg-surface-100" : "bg-surface-700 shadow-card"
        } transition-all duration-300 ease-in-out flex flex-col justify-start`}
      >
        {innerHtml}
      </Link>
    );
  }
  return <>Loading...</>;
}
export default GameCardDetailed;

function Last10Record({ data }: { data?: GameRecord }) {
  if (data) {
    return (
      <div className="flex flex-row gap-1 items-center justify-center text-secondary-300">
        <span className="text-title-lg-bold">
          {data.wins}-
          {data.losses +
            (data.overtimeLosses || 0) +
            (data.shootoutLosses || 0)}
        </span>
        <span className="text-label-lg-medium uppercase text-secondary-400">
          Last 10
        </span>
      </div>
    );
  }
}

function Last10Streak({ data }: { data?: GameOutcome2[] }) {
  console.log("last 10 streak data", data);
  if (!data) return null;
  if (!Array.isArray(data)) return null;
  return (
    <div className="flex flex-row gap-[1.3px] items-center justify-center text-secondary-500 flex-wrap max-w-[96px] gap-y-2 mt-1">
      {data?.map((game, i) => (
        <>
          <div
            className={`text-title-sm-bold uppercase w-[11px] flex justify-center items-center ${oddOutcomeColor(
              game.result
            )}`}
          >
            {formatOddOutcomeShort(game.result)}
          </div>
          {i < data.length - 1 && i !== 4 && (
            <div className="w-[5.4px] flex justify-center items-center">
              <Icon
                name="arrow-right"
                size="2xs"
                className="fill-secondary-500"
              />
            </div>
          )}
        </>
      ))}
    </div>
  );
}

function StartingPlayer({
  data,
  alignment,
}: {
  data?: StartingPlayerInfo;
  alignment: GameAlignment;
}) {
  if (data) {
    return (
      <div className="flex flex-col items-center">
        <div className="flex w-full flex-col items-center">
          <div className="text-title-lg-bold whitespace-nowrap max-w-[96px] truncate">
            {truncateName(data.firstName || "")} {data.lastName || ""}
          </div>
          <div className="flex flex-row gap-1 items-center text-label-lg-medium text-secondary-400">
            <span>#{data.number}</span>
            <span>{data.position}</span>
          </div>
        </div>
        {/* 2 COL STATS */}
        <div className="flex items-center w-full mt-1">
          <div className="flex basis-6/12 flex-col items-center">
            {/* STAT VALUE 1 */}
            <div className="text-title-sm-bold">
              {data.stat1?.value || "N/A"}
            </div>
            {/* STAT VALUE 1 ABBREVATION */}
            <div className="text-label-lg-medium text-secondary-400">
              {data.stat1?.abbreviation || "N/A"}
            </div>
          </div>
          <div className="h-full w-px bg-secondary-700"></div>
          <div className="flex basis-6/12 flex-col items-center">
            {/* STAT VALUE 2 */}
            <div className="text-title-sm-bold">
              {data.stat2?.value || "N/A"}
            </div>
            {/* STAT VALUE 2 ABBREVATION */}
            <div className="text-label-lg-medium text-secondary-400">
              {data.stat2?.abbreviation || "N/A"}
            </div>
          </div>
        </div>
        {/* 1 COL STAT */}
        <div className="flex flex-row justify-center gap-1 items-center w-full mt-1">
          {/* STAT VALUE 3 */}
          <div className="text-title-sm-bold">{data.stat3?.value || "N/A"}</div>
          {/* STAT VALUE 3 ABBREVATION */}
          <div className="text-label-lg-medium text-secondary-400">
            {data.stat3?.abbreviation || "N/A"}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="uppercase flex flex-col items-center justify-center w-full h-full text-label-lg-medium text-secondary-500">
      {alignment} TBD
    </div>
  );
}

function SportsbookCarousel({
  data,
  gameIndex,
  awayTeam,
  homeTeam,
}: {
  data: BestOdds[];
  gameIndex: number;
  awayTeam: string;
  homeTeam: string;
}) {
  const { oddsFormat } = useOddsFormat();

  console.log("unordered", data);

  const orderedData = React.useMemo(() => {
    if (data.length === 0) return [];

    const rotatedIndex = gameIndex % 3;
    const rotatedData = [
      ...data.slice(rotatedIndex),
      ...data.slice(0, rotatedIndex),
    ];
    return rotatedData;
  }, [data, gameIndex]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [bg, setBg] = React.useState<string | undefined>(
    orderedData[activeIndex].sportsbookLogoUrl
  );
  const splideRef = React.useRef<Splide>(null);

  function changeSlide(idx: number) {
    setActiveIndex(idx);
  }

  React.useEffect(() => {
    setBg(orderedData[activeIndex].sportsbookLogoUrl);
  }, [activeIndex]);

  const handlePaginationClick = (index: number) => {
    splideRef.current?.splide?.go(index);
  };

  return (
    <>
      <div
        className="absolute inset-0 bg-transparent opacity-30 z-0 w-full h-full rounded-md bg-no-repeat bg-center bg-cover transition-all 300ms ease-in-out"
        style={{ backgroundImage: `url(${bg})` }}
      ></div>
      <div className="absolute inset-0 bg-transparent opacity-100 z-0 w-full h-full rounded-md bg-no-repeat bg-center bg-cover bg-[url('/images/game/sportsbook-bg.png')]"></div>
      <div className="relative -ml-3 -mr-3 desktop-grid:mx-0 overflow-hidden h-full z-[0]">
        <Splide
          ref={splideRef}
          hasTrack={false}
          onMove={(splide, index, prev, dest) => {
            changeSlide(index);
          }}
          options={{
            type: "slide",
            arrows: false,
            pagination: false,
            lazyLoad: "nearby",
            drag: true,
            perPage: 1,
            perMove: 1,
            snap: true,
            flickMaxPages: 1,
          }}
          className="h-full"
        >
          <SplideTrack className="rounded-none desktop-grid:rounded-xl overflow-hidden h-full">
            {orderedData.length > 0 &&
              orderedData?.map((slide, i) => {
                if (!slide.odd) return;
                const showAbbrevation: boolean =
                  slide.odd.type !== LineType.Total;
                const abbreviation: string =
                  slide.odd.alignment === GameAlignmentType.Away
                    ? awayTeam
                    : homeTeam;
                const formattedOdd = formatOdd(oddsFormat, slide.odd);
                return (
                  <SplideSlide key={i} className="h-full relative">
                    {/* <div
                    className="inset-0 -top-8 opacity-50 bg-no-repeat bg-cover bg-top w-full h-full absolute z-0"
                    style={{
                      backgroundImage: slide.sportsbookLogoUrl
                        ? `url('${slide.sportsbookLogoUrl}')`
                        : undefined,
                    }}
                  ></div> */}

                    <div className="min-h-0 h-full flex flex-col justify-start items-center pt-0 relative z-[1]">
                      <div className="text-title-sm-bold text-white inline-flex justify-center items-center gap-1">
                        {showAbbrevation && <span>{abbreviation} </span>}
                        <div className="flex flex-row gap-1 justify-center items-center">
                          {formattedOdd?.type === LineType.Moneyline
                            ? formattedOdd?.payout
                            : formattedOdd?.value}{" "}
                          <span className="text-secondary-500 text-label-sm-medium">
                            {formattedOdd?.type === LineType.Moneyline
                              ? undefined
                              : formattedOdd?.payout}
                          </span>
                        </div>
                      </div>
                      <div className="text-label-lg-bold text-white">
                        @ {slide.sportsbook}
                      </div>
                      <div className="mt-1">
                        <a
                          target="_blank"
                          href={slide.sportsbookUrl || undefined}
                          rel="noreferrer"
                          className="btn btn-2xs btn-filled-primary"
                        >
                          Bet Now
                        </a>
                      </div>
                    </div>
                  </SplideSlide>
                );
              })}
          </SplideTrack>

          <Pagination
            totalSlides={data.length}
            activeSlide={activeIndex}
            onPaginationClick={handlePaginationClick}
          />
        </Splide>
      </div>
    </>
  );
}

interface PaginationProps {
  totalSlides: number;
  activeSlide: number;
  onPaginationClick: (index: number) => void;
}
function Pagination(props: PaginationProps) {
  return (
    <div className="custom-pagination p-1 absolute bottom-[18px] left-0 right-0 z-20 flex justify-center gap-1">
      {Array.from({ length: props.totalSlides }, (_, index) => (
        <button
          key={index}
          className={`relative h-[3px] rounded-[2px] transition-all overflow-hidden ${
            props.activeSlide === index
              ? "w-4 bg-primary-500"
              : "w-2 bg-surface-700"
          }`}
          onClick={() => props.onPaginationClick(index)}
          title={`Go to slide ${index + 1}`}
        >
          {/* <div className="absolute inset-0 bg-surface-700"></div> */}
        </button>
      ))}
    </div>
  );
}
